import { createGlobalStyle } from 'styled-components';

import { px2vw } from '../utils/functions';
import theme from './theme.styled';

export const Global = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  :root {
    font-size: ${px2vw(24)};
    --font-size-small: 12px;
    --font-size-medium: 14px;
    --font-size-base: 16px;
    --main-background: #ffffff;
    --secondary-background: #fafafa;
    --main-black: #000;
    --main-white: #fff;
    --main-blue: #007aff;
    --main-green: #32de9c;
    --main-violet: #7b61ff;
    --main-gray: #efefef;
    --secondary-gray: #636363;
    --main-red: #ff0000;
    --block-background: #ffffff;
    --card-background: #ffffff;
    --input-background: #f7f9ff;
    --hover-background: #f5f5f5;
    --accent-background: #ebeffd;
    --subtle-background: #f8f9fa;
    --box-shadow-soft: rgba(0, 0, 0, 0.08) 1px 2px 10px;
    --box-shadow-button: rgba(204, 204, 204, 0) 0px 0px 0px 1px, rgba(0, 0, 0, 0.04) 0px 0px 0px 1px, rgba(0, 0, 0, 0.12) 0px 1px 4px 0px;
    --transition: 0.3s ease-in-out;
    --hover-opacity: 0.8;
    --border-thin: 1px solid rgb(224, 224, 224);
    --border-color: #e0e2e9;
    --tooltip-background: #222;
    --tooltip-color: #fff;
    --text-on-accent: #fff;
    --text-muted: #757575;
    --text-tertiary: #9393a0;
    --shadow-color: rgba(0, 0, 0, 0.08);
    --shadow-medium: rgba(149, 157, 165, 0.2) 0px 8px 24px;
    --overlay-light: rgba(0, 0, 0, 0.04);
    --border-light: #e5e7eb;
    --surface-elevated: #f3f3f3;
    --focus-ring: rgba(1, 85, 250, 0.3);
    --scrollbar-thumb: rgba(0, 0, 0, 0.2);
    --scrollbar-track: transparent;

    /* Status colours: badges, notes, destructive actions */
    --success-text: #16a34a;
    --success-background: #ecfdf5;
    --success-border: #bbf7d0;
    --warning-text: #b45309;
    --warning-background: #fffbeb;
    --warning-border: #fde68a;
    --danger-text: #d32f2f;
    --danger-background: #fdecea;
    --danger-border: #f5c2c0;
    --info-text: #2563eb;
    --info-background: #eff6ff;
    --info-border: #bfdbfe;
    --violet-text: #7950f2;
    --violet-background: #f3f0ff;

    /* Vertical spacing between top tabs nav and tab page content (Content / Posts / Blog / Store / Photo / etc) */
    --tab-content-top: 16px;


    @media (min-width: 768px) {
      font-size: ${px2vw(18)};
    }

    @media (min-width: 1024px) {
      font-size: ${px2vw(16)};
    }
  }

  /* ============================================
     DARK THEME — перевизначаємо тільки кольори
     Всі компоненти що вже використовують var()
     підхвачують dark mode автоматично
  ============================================ */
  [data-theme="dark"] {
    --main-background: #1c1c21;
    --secondary-background: #141417;
    --main-black: #f0f0f0;
    --main-white: #1a1a1f;
    --main-gray: #2a2a32;
    --secondary-gray: #b5bac1;
    --border-thin: 1px solid rgba(255, 255, 255, 0.08);
    --border-color: rgba(255, 255, 255, 0.08);
    --box-shadow-soft: rgba(0, 0, 0, 0.4) 1px 2px 10px;
    --box-shadow-button: rgba(255, 255, 255, 0.04) 0px 0px 0px 1px, rgba(0, 0, 0, 0.3) 0px 1px 4px 0px;
    --block-background: #313338;
    --card-background: #252528;
    --input-background: #25252d;
    --hover-background: #35373c;
    --accent-background: #2a2a40;
    --subtle-background: #25252d;
    --tooltip-background: #e0e0e0;
    --tooltip-color: #1a1a1f;
    --text-on-accent: #fff;
    --text-muted: #949ba4;
    --text-tertiary: #6a6a7a;
    --shadow-color: rgba(0, 0, 0, 0.4);
    --shadow-medium: rgba(0, 0, 0, 0.3) 0px 8px 24px;
    --overlay-light: rgba(255, 255, 255, 0.04);
    --border-light: rgba(255, 255, 255, 0.06);
    --surface-elevated: #2f2f38;
    --focus-ring: rgba(59, 130, 246, 0.4);
    --scrollbar-thumb: rgba(255, 255, 255, 0.15);
    --scrollbar-track: transparent;
    --success-text: #4ade80;
    --success-background: rgba(34, 197, 94, 0.15);
    --success-border: rgba(34, 197, 94, 0.3);
    --warning-text: #ffd873;
    --warning-background: rgba(255, 193, 7, 0.12);
    --warning-border: rgba(255, 193, 7, 0.3);
    --danger-text: #f87171;
    --danger-background: rgba(211, 47, 47, 0.16);
    --danger-border: rgba(211, 47, 47, 0.4);
    --info-text: #7fb4ff;
    --info-background: rgba(37, 99, 235, 0.15);
    --info-border: rgba(37, 99, 235, 0.4);
    --violet-text: #a48cf7;
    --violet-background: rgba(121, 80, 242, 0.16);
    background-color: var(--main-background);
    color-scheme: dark;
  }

  /* ============================================
     DARK THEME — Third-party component overrides
  ============================================ */
  [data-theme="dark"] {
    /* MUI components */
    .MuiPaper-root {
      background-color: var(--card-background) !important;
      color: var(--main-black) !important;
    }

    .MuiDrawer-anchorBottom .MuiDrawer-paper {
      background-color: var(--card-background) !important;
    }

    .MuiLinearProgress-colorPrimary {
      background-color: var(--main-gray) !important;
    }

    /* react-datepicker */
    .react-datepicker {
      background-color: var(--card-background);
      border-color: var(--border-color);
      color: var(--main-black);
    }
    .react-datepicker__header {
      background-color: var(--secondary-background);
      border-bottom-color: var(--border-color);
    }
    .react-datepicker__day,
    .react-datepicker__day-name,
    .react-datepicker__current-month,
    .react-datepicker-time__header {
      color: var(--main-black);
    }
    .react-datepicker__day:hover {
      background-color: var(--hover-background);
    }
    .react-datepicker__day--selected {
      background-color: var(--main-blue) !important;
      color: #fff !important;
    }
    .react-datepicker__day--outside-month,
    .react-datepicker__day--disabled {
      color: var(--text-tertiary);
    }
    .react-datepicker__input-container input {
      background-color: var(--input-background);
      color: var(--main-black);
    }
    .react-datepicker__navigation-icon::before {
      border-color: var(--secondary-gray);
    }
    .react-datepicker__triangle::before,
    .react-datepicker__triangle::after {
      border-bottom-color: var(--secondary-background) !important;
    }

    /* react-datepicker — the "Time" column (missed by the rules above) */
    .react-datepicker__time-container {
      border-left-color: var(--border-color);
    }
    .react-datepicker__time,
    .react-datepicker__time-box {
      background-color: var(--card-background);
    }
    .react-datepicker__time-list {
      background-color: var(--card-background) !important;
    }
    .react-datepicker__time-list-item {
      background-color: var(--card-background) !important;
      color: var(--main-black) !important;
    }
    .react-datepicker__time-list-item:hover {
      background-color: var(--hover-background) !important;
    }
    .react-datepicker__time-list-item--selected {
      background-color: var(--main-blue) !important;
      color: #fff !important;
    }
    .react-datepicker__time-list-item--disabled {
      color: var(--text-tertiary) !important;
    }

    /* Quill editor */
    .ql-editor {
      color: var(--main-black);
      background: var(--input-background);
    }
    /* keep the rich-text inside the site preview rendering as the published (light) page */
    .preview .ql-editor {
      color: #000000;
      background: transparent;
    }
    .ql-toolbar.ql-snow {
      background: var(--block-background);
      border-color: var(--border-color) !important;
    }
    .ql-container.ql-snow {
      border-color: var(--border-color);
    }
    .ql-snow .ql-stroke {
      stroke: var(--main-black) !important;
    }
    .ql-snow .ql-fill {
      fill: var(--main-black) !important;
    }
    .ql-snow .ql-picker-label {
      color: var(--main-black);
    }
    .ql-snow .ql-picker-options {
      background-color: var(--card-background);
      border-color: var(--border-color);
    }
    .ql-editor.ql-blank::before {
      color: var(--text-muted);
    }

    /* RDW editor toolbar icons */
    .rdw-option-wrapper img {
      filter: invert(0.85);
    }
    .rdw-dropdown-carettoclose {
      border-bottom-color: var(--main-black);
    }
    .rdw-link-modal,
    .rdw-image-modal,
    .rdw-emoji-modal {
      color: var(--main-black);
    }
    .rdw-link-modal-input,
    .rdw-image-modal-url-input,
    .rdw-image-modal-size-input {
      background-color: var(--input-background);
      color: var(--main-black);
    }

    /* Demo editor */
    .demo-editor {
      border-color: var(--border-color) !important;
      color: var(--main-black);
    }

    /* Scrollbar styling */
    ::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    ::-webkit-scrollbar-track {
      background: var(--scrollbar-track);
    }
    ::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb);
      border-radius: 3px;
    }
  }

  /* THEME TRANSITION — вмикається тільки під час
     перемикання теми через JS клас */
     
  .theme-transition,
  .theme-transition *,
  .theme-transition *::before,
  .theme-transition *::after {
    transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease !important;
  }

  html,
  body,
  #root {
    height: 100%;
  }

  html {
    /* Adjust font size */
    font-size: 100%;
    -webkit-text-size-adjust: 100%;
    /* Font varient */
    font-variant-ligatures: none;
    -webkit-font-variant-ligatures: none;
    /* Smoothing */
    text-rendering: optimizeLegibility;
    -moz-osx-font-smoothing: grayscale;
    //font-smoothing: antialiased;
    -webkit-font-smoothing: antialiased;
    text-shadow: rgba(0, 0, 0, .01) 0 0 1px;
  }

  section.preview {
    @media (min-height: 795px) {
      //height: 650px !important;
    }
  }

  div.preview {
    @media (min-height: 795px) {
      height: 650px !important;
    }
  }
  
  body {
    overflow-y: auto !important;
    scrollbar-gutter: stable;
    font-family: ${theme.fontFamily.default};
    font-size: ${theme.fontSize.default};
  }
  
  .react-datepicker-popper {
    z-index: 11 !important;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .MuiPopover-paper {
    z-index: 65535 !important;
    //@media (max-width: 576px) {
    //  max-width: 100vw !important;
    //  min-width: 100vw !important;
    //  max-height: 100% !important;
    //}
  }
  
  .MuiDialog-root {
    z-index: 65535 !important;
  }

  .MuiDrawer-paperAnchorBottom {
    @media (max-width: 576px) {
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
      max-height: 98%;
    }
  }

  
  
  /* Pagination */

  .react-pagination {
    margin: 35px auto 15px;
    display: flex;
    align-items: center;
    list-style: none;
    outline: none;
  }
  .react-pagination > .react-pagination__active > a{
    background-color: #6b85fb; 
    color: #ffffff;
  }
  .react-pagination > li > a{
    padding: 5px 10px;
    outline: none;
    cursor: pointer;
  }
  .react-pagination > .react-pagination__active > a,
  .react-pagination  > .react-pagination__active > span, 
  .react-pagination  > .react-pagination__active > a:hover, 
  .react-pagination  > .react-pagination__active > span:hover, 
  .react-pagination  > .react-pagination__active > a:focus, 
  .react-pagination  > .react-pagination__active > span:focus{
    background-color: #6b85fb;
    border-radius: 3px;
    outline: none ;
  }
  .react-pagination > li > a, 
  .react-pagination  > li > span{
    color: #a6aacc
  }
  .react-pagination > li:first-child > a, 
  .react-pagination  > li:first-child > span, 
  .react-pagination  > li:last-child > a, 
  .react-pagination  > li:last-child > span{
    border-radius: unset;
  }

  .react-pagination > li:first-child {
    margin-right: 15px;
  }

  .react-pagination > li:last-child {
    margin-left: 15px;
  }

  


  /* react-datepicker */

  .react-datepicker {
    font-size: 0.7em;
  }
  .react-datepicker__header {
    padding-top: 0.8em;
  }
  .react-datepicker__month {
    margin: 0.4em 1em;
  }
  .react-datepicker__day-name, .react-datepicker__day {
    width: 1.9em;
    line-height: 1.9em;
    margin: 0.166em;
  }
  .react-datepicker__current-month {
    font-size: 1em;
  }
  //.react-datepicker__navigation {
  //  top: 1em;
  //  line-height: 1.7em;
  //  border: 0.45em solid transparent;
  //}
  //.react-datepicker__navigation--previous {
  //  border-right-color: #ccc;
  //  left: 1em;
  //}
  //.react-datepicker__navigation--next {
  //  border-left-color: #ccc;
  //  right: 1em;
  //}

  .react-datepicker-time__header {
    font-size: 1em;
  }
  
  .react-datepicker__input-container {
    input {
      border: 1px solid var(--border-color);
      border-radius: 8px;
      padding: 8px 16px;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: -0.4px;
      color: var(--main-black);
      outline: none;
      -webkit-transition: all .2s;
      transition: all .2s;
      display: block;
      width: 100%;
      min-width: auto;
    }
  }


  .react-datepicker-ignore-onclickoutside {
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.4px;
    color: var(--main-black);
    outline: none;
    -webkit-transition: all .2s;
    transition: all .2s;
    display: block;
    width: 100%;
    min-width: auto;
  }

  .ButtonLinkMenuItem {
    &:hover {
      .ButtonLinkMenuItemIcon {
          fill: #fff;
      }
    }
  }

  .MuiLinearProgress-barColorPrimary {
    background: var(--gradient, linear-gradient(84deg, #CA55F3 -2.71%, #FF53A5 31.8%, #FF763C 81.33%, #FFF859 104.15%));
  }

  .MuiLinearProgress-colorPrimary {
    height: 12px !important;;
    border-radius: 40px !important;;
    background-color: var(--main-gray) !important;
  }

  

  @media (max-width: 568px) {
    #fb-root {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }

    .fb-root {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }

    #fb-reset {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }
    
    .fb-reset {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }

    #fb-customerchat {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }
    
    .fb-customerchat {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }

    #facebook-jssdk {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }
    
    .facebook-jssdk {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }

    .fb-customerchat, .fb_invisible_flow, .fb_iframe_widget ,.fb_iframe_widget_fluid {
      display: none !important;
      z-index: -1 !important;
      opacity: 0 !important;
    }
  }

  .rdw-editor-toolbar {
    padding: 6px 5px 0;
    border-radius: 2px;
    border: 1px solid var(--border-color);
    display: flex;
    justify-content: flex-start;
    background: var(--main-background);
    flex-wrap: wrap;
    font-size: 15px;
    margin-bottom: 5px;
    user-select: none;
  }

  
  
  .rdw-inline-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .rdw-option-wrapper {
    border: 1px solid var(--border-color);
    padding: 5px;
    min-width: 25px;
    height: 20px;
    border-radius: 2px;
    margin: 0 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: var(--main-background);
    text-transform: capitalize;
  }


  .demo-content {
    border: 1px solid var(--border-color);
    padding: 5px;
    width: 100%;
    border-radius: 2px;
    resize: none;
    height: 155px;
    overflow: hidden;
  }

  .demo-editor {
    height: 225px !important;
    border: 1px solid var(--border-color) !important;
    padding: 5px !important;
    border-radius: 2px !important;
    margin-bottom: 6px;
    overflow-x: auto;
    
    img {
      width: 120px !important;
      height: auto !important;
    }
  }

  .rdw-fontsize-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .rdw-fontsize-dropdown {
    min-width: 40px;
  }

  .rdw-dropdown-wrapper {
    height: 30px;
    background: var(--main-background);
    cursor: pointer;
    border: 1px solid var(--border-color);
    border-radius: 2px;
    margin: 0 3px;
    text-transform: capitalize;
  }

  .rdw-dropdown-selectedtext {
    display: flex;
    position: relative;
    height: 100%;
    align-items: center;
    padding: 0 5px;
  }

  .rdw-dropdown-carettoopen {
    height: 0;
    width: 0;
    position: absolute;
    top: 35%;
    right: 10%;
    border-top: 6px solid var(--main-black);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
  }

  .rdw-dropdown-optionwrapper {
    z-index: 100;
    position: relative;
    border: 1px solid var(--border-color);
    width: 98%;
    background: var(--main-background);
    border-radius: 2px;
    margin: 0;
    padding: 0;
    max-height: 250px;
    overflow-y: scroll;
  }

  .rdw-fontsize-option {
    display: flex;
    justify-content: center;
  }

  .rdw-dropdownoption-default {
    min-height: 25px;
    display: flex;
    align-items: center;
    padding: 0 5px;
  }

  .rdw-list-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .rdw-emoji-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    position: relative;
    flex-wrap: wrap;
  }

  .rdw-emoji-modal {
    overflow: auto;
    position: absolute;
    top: 35px;
    left: 5px;
    display: flex;
    flex-wrap: wrap;
    width: 235px;
    height: 180px;
    border: 1px solid var(--border-color);
    padding: 15px;
    border-radius: 2px;
    z-index: 100;
    background: var(--main-background);
    box-shadow: var(--box-shadow-soft);
  }

  .rdw-emoji-icon {
    margin: 2.5px;
    height: 24px;
    width: 24px;
    cursor: pointer;
    font-size: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .rdw-link-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    position: relative;
    flex-wrap: wrap;
  }

  .rdw-image-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    position: relative;
    flex-wrap: wrap;
  }

  .rdw-image-modal {
    position: absolute;
    top: 35px;
    left: 5px;
    display: flex;
    flex-direction: column;
    width: 235px;
    border: 1px solid var(--border-color);
    padding: 15px;
    border-radius: 2px;
    z-index: 100;
    background: var(--main-background);
    box-shadow: var(--box-shadow-soft);
  }

  .rdw-image-modal-header {
    font-size: 15px;
    margin: 10px 0;
    display: flex;
  }

  .rdw-image-modal-header-option {
    width: 50%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  .rdw-image-modal-header-label-highlighted {
    background: #6EB8D4;
    border-bottom: 2px solid #0a66b7;
  }

  .rdw-image-modal-url-section {
    display: flex;
    align-items: center;
  }

  .rdw-image-modal-url-input {
    width: 90%;
    height: 35px;
    margin: 15px 0 12px;
    border: 1px solid var(--border-color);
    border-radius: 2px;
    font-size: 15px;
    padding: 0 5px;
  }

  .rdw-image-mandatory-sign {
    color: var(--main-red);
    margin-left: 3px;
    margin-right: 3px;
  }

  .rdw-image-modal-size {
    align-items: center;
    display: flex;
    margin: 8px 0;
    justify-content: space-between;
  }

  .rdw-image-modal-size-input {
    width: 40%;
    height: 20px;
    border: 1px solid var(--border-color);
    border-radius: 2px;
    font-size: 12px;
  }

  .rdw-image-modal-btn-section {
    margin: 10px auto 0;
  }

  .rdw-image-modal-btn {
    margin: 0 5px;
    width: 75px;
    height: 30px;
    border: 1px solid var(--border-color);
    border-radius: 2px;
    cursor: pointer;
    background: var(--main-background);
    text-transform: capitalize;
  }

  .rdw-image-modal-btn:disabled {
    background: var(--main-gray);
  }

  .rdw-text-align-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .rdw-link-modal {
    position: absolute;
    top: 35px;
    left: 5px;
    display: flex;
    flex-direction: column;
    width: 235px;
    height: 205px;
    border: 1px solid var(--border-color);
    padding: 15px;
    border-radius: 2px;
    z-index: 100;
    background: var(--main-background);
    box-shadow: var(--box-shadow-soft);
  }

  .rdw-link-modal-label {
    font-size: 15px;
  }

  .rdw-link-modal-input {
    margin-top: 5px;
    border-radius: 2px;
    border: 1px solid var(--border-color);
    height: 25px;
    margin-bottom: 15px;
    padding: 0 5px;
  }

  .rdw-link-modal-target-option {
    margin-bottom: 20px;
  }

  .rdw-link-modal-target-option > span {
    margin-left: 5px;
  }

  .rdw-link-modal-buttonsection {
    margin: 0 auto;
  }

  .rdw-link-modal-btn:disabled {
    background: var(--main-gray);
  }

  .rdw-link-modal-btn {
    margin-left: 10px;
    width: 75px;
    height: 30px;
    border: 1px solid var(--border-color);
    border-radius: 2px;
    cursor: pointer;
    background: var(--main-background);
    text-transform: capitalize;
  }

  .rdw-link-modal-buttonsection {
    margin: 0 auto;
  }

  .rdw-link-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    position: relative;
    flex-wrap: wrap;
  }

  .slick-dots {
    bottom: 35px !important;
  }
  
  .slick-dots li button:before {
    font-size: 10px !important;
  }

  .slick-dots li.slick-active button:before {
    color: #017afd !important;
  }



  .ql-toolbar.ql-snow {
    border: 1px solid var(--border-color) !important;
    border-radius: 8px 8px 0 0;
  }

  .ql-snow .ql-tooltip {
    left: 0 !important;
  }

  .ql-container.ql-snow {
    border: 1px solid var(--border-color);
    border-radius: 0 0 8px 8px;
  }

  .ql-editor {
    min-height: 150px;
    padding: 12px !important;
  }

  .quill--modal--extend {
    margin-top: 50px;
  }

  .quill--modal--extend .ql-editor {
    //min-height: 470px;
    //height: auto !important;
    min-height: 70vh;
    //white-space: pre-wrap !important; /* Забезпечує коректне відображення переносів рядків */
    //word-break: break-word !important; /* Запобігає розриву слів */
  }
  
  .quill--large .ql-editor {
    min-height: 300px;
  }

  .quill--large .ql-container.ql-snow{
    border: none;
    padding-left: 0 !important;
  }
  .quill--large .ql-editor.ql-blank{
    padding-left: 0 !important;
  }

  .quill--large .ql-toolbar.ql-snow{
    border: none !important;
    padding-left: 0;
  }

  .ql-container {
    height: initial !important;
  }
  
  .ql-snow .ql-editor h1 {
     font-weight: normal;
  }

  .h1 {
    font-weight: normal;
  }
  
  
  .tooltipAddSection {
    padding: 10px !important;
    width: 243px;
    background: var(--card-background) !important;
    color: var(--main-black) !important;
    border-radius: 8px !important;
    z-index: 66666;

    box-shadow: var(--box-shadow-soft);
    opacity: 1 !important;
    //border: 1px solid #d3d3d3 !important;

    //border-left: 15px solid transparent;
    //border-right: 15px solid transparent;
    //border-bottom: 15px solid white;

    //filter: drop-shadow(1px 2px 10px rgb(220 220 220 / 8%));
    
    &:after {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      background: transparent !important;
    }
  }
  
  .react-rater{
    margin-top: -14px;
  }

  .react-rater-star {
    font-size: 45px;
  }

  .react-rater-star.will-be-active {
    color: #fda429 !important;
    font-size: 45px;
  }
  
  .react-rater-star.is-active {
    color: #fda429 !important;
    font-size: 45px;
  }
  
  .tooltipCaption {
    width: 200px !important;
  }

  .css-tooltip {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .css-tooltip::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: var(--tooltip-background);
    color: var(--tooltip-color);
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.4;
    white-space: normal;
    width: 200px;
    text-align: center;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s, visibility 0.15s;
    z-index: 9999;
  }

  .css-tooltip:hover::after {
    opacity: 1;
    visibility: visible;
  }

  .MuiTypography-body1 {
    font-size: initial !important;
  }
  
  .MuiPopover-paper {
    border-radius: 16px !important;
  }

  .popup-toolbar {
    z-index: 100000 !important;

    .MuiPopover-paper {
      overflow: visible;
      border-radius: 16px !important;
    }
  }


  .slider-section-swiper--bottom-pagination {
    .swiper-horizontal>.swiper-pagination-bullets, .swiper-pagination-bullets.swiper-pagination-horizontal, .swiper-pagination-custom, .swiper-pagination-fraction {
      display: none;
      bottom: 0;
      margin-left: -12px;
    }
  }

  .swiper-button-prev,
  .swiper-button-next {
    color: var(--main-black);
    width: 25px;
    height: 25px;
  }

  .swiper-button-prev::after,
  .swiper-button-next::after {
    font-size: 20px;
  }

  .sliderSwiper, .templatesSliderSwiper {
    .swiper-slide {
      width: auto!important;

      &:last-child {
        padding-right: 26px !important;
      }
    }
  }

  .templatesSliderSwiper {


    .swiper-wrapper {
      //overflow: auto hidden;
    }
  }
  
  
  
  .digitalSlider {
    .swiper-slide {
      width: 100% !important;
    }
  }

  .ql-editor ol, .ql-editor ul {
    padding-left: 0 !important;
  }

  .editor-container {
    border: 1px solid var(--border-color);
    padding: 10px;
    min-height: 150px;
  }

  .editor-input {
    outline: none;
    min-height: 150px;
  }

  .editor-placeholder {
    color: var(--secondary-gray);
  }

  .ql-tooltip {
    transform: translateX(30%) !important;
  }
`;

export default Global;
